import { Injectable, ComponentRef } from "@angular/core";

import { Observable, ReplaySubject, Subject } from "rxjs";
import { DashboardItemType } from "../bfx-common/models/dashboard-item-type.enum";
import { GoldenLayoutAdditionMode } from "./golden-layout-addition-mode";
import { GoldenLayoutPopout } from "./golden-layout-popout";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";
import { TranslateService } from "@ngx-translate/core";
import { OENExternalCommand } from "../order-entry/data/enums/oe-external-command.enum";

import { NativeDesktopSupport, ZOOM_VALUES } from "../bfx-native-desktop/native-desktop-support";
import { GLComponentAlertStatus } from "./golden-layout-component";
import { USERSETTINGS_CATEGORY_BMARKSUMMARY } from "../bfx-user-settings/bfx-user-settings.component";
import { PromiseSet } from "shared/provider-webworker/web_workers/shared/promise_set";
import { NullProtoObject } from "../../shared/helper-services/null-proto-object";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
import {
  BfxUserSettingsService,
  BfxUserSettingsComponentState,
  BfxUserSettingsCategoryIndex,
  BfxUserSettingsOrderSubCategoryIndex,
  BfxUserSettingsNotificationsSubCategoryIndex,
} from "../bfx-user-settings/bfx-user-settings.service";
import {
  ProfilePromiseActionService,
  ProfileSettingKeys,
  ActiveUserProfilePromiseService,
  ActiveUser,
} from "shared/cubiq-services";
import { ProfileSettingImpl } from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
import { BfxDialogData } from "../bfx-dialogs/models/bfx-dialog-data.model";
const logger = LoggerService.for(__dirname);

export const CONFIG_PROPERTY_POPOUT_COMPONENT_ID = "popoutComponentId";

export enum GLPopoutState {
  EMBEDDED,
  POPOUT,
}

export enum GLPopoutUndockBehaviour {
  INHERIT_FROM_DOCK_CONTAINER,
  APPLY_BOUNDING_SIZE,
}

export const OB_TAB_TITLE = "Order Blotter";
export const OB_TAB_TITLE_SHORT = "OB";
export const AM_TAB_TITLE = "Algo Monitor";
export const AM_TAB_TITLE_SHORT = "AM";
export const CT_TAB_TITLE = "Client Trade Blotter";
export const CT_TAB_TITLE_SHORT = "CT";
export const RT_TAB_TITLE = "Risk Trade Blotter";
export const RT_TAB_TITLE_SHORT = "RT";
export const DI_TAB_TITLE = "Dealer Intervention";
export const DI_TAB_TITLE_SHORT = "DI";
export const OL_TAB_TITLE = "Order Ladder";
export const OL_TAB_TITLE_SHORT = "OL";
export const BM_TAB_TITLE = "Benchmark Summary";
export const BM_TAB_TITLE_SHORT = "BM";

export class GLPopoutConfiguration {
  public popoutComponentId: string;

  /**
   * Flag indicating if the popout is resizable
   */
  public popoutResizable: boolean = true;
  /**
   * Array indicating if in which directions the popout is resizable
   */
  public popoutResizableDirections: Array<string> = null;

  public disablePopout: boolean = false;

  /**
   * An object containing x and y attributes as coordinates of the popout
   */
  public position: { x: number; y: number };

  /**
   * An object containing width and height attibutes as preferred
   * size of the popout when first displayed
   */
  public preferredSize: any;

  /**
   * An object containing width and height attibutes as minimal
   * size of the popout when first displayed. The popout will not
   * be able to scale down bellow this parameters.
   */
  public minimumSize: any;

  /**
   * An object containing width and height attibutes as maximum
   * size of the popout when first displayed. The popout will not
   * be able to scale down above this parameters.
   * Works only on desktop.
   */
  public maximumSize: any;

  /**
   * Arbitrary data passed on to the embedded popout component
   * when initialized. as data may form part of profile configuration
   * Anything non-serializable *must* be marked "enumerable: false" per:
   *
   * Object.create({}, { x: { value: 'x', enumerable: false } })
   */
  public data: any;

  /**
   * What is the behaviour of the popout when undocked in terms of it's size.
   */
  public undockBehaviour: GLPopoutUndockBehaviour =
    GLPopoutUndockBehaviour.INHERIT_FROM_DOCK_CONTAINER;

  /**
   * The state of the popout when it initially appears. Use this property to
   * indicate if the popout should first be embedded or undocked.
   */
  public initialState: GLPopoutState = GLPopoutState.EMBEDDED;

  public ladderBlotter: any = null;

  /**
   * A flag indicating that activation should be prevented if a
   * popout with same identifier already exists.
   */
  public preventActivation: boolean = false;

  /**
   * a flag indicating that after popup is added, main window should
   * be restored to position in stack (e.g. ops int is less impor)
   */
  public restoreMainWindow: boolean = false;

  /**
   * Originating action event that triggered to open this popout
   */
  public sourceEvent: any = null;

  /**
   * set during glInit() to abandon opening of popout
   * will result in cleanup in GoldenLayoutPopupContaner.onPopoutEvent()
   *
   * message will be displayed as a part of a notification
   * error.loading.generic": "Error loading {{0}}",
   */
  public glInitError?: Error = null;

  /**
   * Can this window be restored when session profile is restored on login? Will be set to false
   * automatically if showDock is disabled when window is created.
   */
  public canRestore: boolean = true;

  /**
   * Window on desktop platform open where the mouse was clicked before they are
   * activated. This flags turns off this feature.
   */
  public ignoreScreenEvent: boolean = false;

  public errorDialogOnMultiple: boolean = false;

  constructor(readonly opts?: Partial<GLPopoutConfiguration>) {
    if (opts != null && typeof opts === "object") {
      Object.keys(opts).forEach((key) => {
        this[key] = opts[key];
      });
    }
  }
}

export class GLPopoutEvent {
  constructor(
    public component: ComponentRef<any>,
    public stack: any,
    public config: any,
    public popout: ComponentRef<GoldenLayoutPopout>,
  ) {}
}

export class GLBadgeUpdateEvent {
  constructor(
    public component: ComponentRef<any>,
    public badgeCount: number,
    public status: GLComponentAlertStatus,
  ) {}
}

@Injectable()
export class GoldenLayoutService {
  private popouts: NullProtoObject<ComponentRef<any>> = Object.create(null);
  private glPopouts: NullProtoObject<GoldenLayoutPopout> = Object.create(null);
  private tabs: NullProtoObject<any> = Object.create(null);
  private ladderTabs: NullProtoObject<any> = Object.create(null);
  private popoutCounter: number = 1;
  private componentStateFn: Function;
  private profileConfiguration: Promise<any> = new Promise<any>(this.loadActiveUser.bind(this));
  private additionalProfileConfiguration: any = {};
  private _ready: PromiseSet<void> = new PromiseSet<void>();

  private notificationData: any = {};
  public notificationDataSubject: Subject<any> = new ReplaySubject(1);

  /**
   * The status in GLBadgeUpdateEvent added to the subjectBadge is used for popout(desktop), it should be calculated
   * without takeing into account whether the component is active (so only based on badge count).
   * Thus it is different than the tab status.
   */
  public subjectBadge: Subject<GLBadgeUpdateEvent> = new Subject<GLBadgeUpdateEvent>();
  public subjectFlash: Subject<void> = new Subject<void>();
  // debug
  private orderEntrySource = new Subject<{
    cmd: OENExternalCommand;
    data: any;
  }>();
  public orderEntryCmd$ = this.orderEntrySource.asObservable();

  private dialogSubject: Subject<BfxDialogData> = new Subject<BfxDialogData>();
  public dialogCtrl$ = this.dialogSubject.asObservable();

  public singleInstanceLocations: any = {};
  public singleInstanceData: any = {};

  // this is used by golden-layout to establish count of popups
  public popoutNativeShown = new Subject<void>();
  public popoutNativeShown$ = this.popoutNativeShown.asObservable();

  private profileActionSubscription = new Subject<boolean>();
  public savedProfileAction$ = this.profileActionSubscription.asObservable();

  private titleUpdateSubject = new Subject<string>();
  /** Emits blotterIds when title of a blotter has changed  */
  public titleUpdateChange$: Observable<string> = this.titleUpdateSubject.asObservable();

  private _goldenLayout: {
    // can't use injector / DI @Host for this even import file due to real circular deps
    newFromDashboard(descriptor: any): void | PromiseLike<void>;
  } = null;

  public get goldenLayout() {
    // please avoid this
    return this._goldenLayout;
  }

  public set goldenLayout(goldenLayout) {
    // please avoid this- set once from golden-layout.ts angular compoennt
    if (this._goldenLayout == null) {
      this._goldenLayout = goldenLayout;
    }
  }

  private _goldenLayoutPopoutContainer: {
    // can't use injector / DI @Host for this even import file due to real circular deps
    onPopoutEvent(event: GLPopoutEvent): void;
    onPopoutFocus(
      popoutComponentId: string,
      popoutComponent: ComponentRef<any>,
      popout: GoldenLayoutPopout,
    ): void;
  } = null;

  public get goldenLayoutPopoutContainer() {
    // please avoid this
    return this._goldenLayoutPopoutContainer;
  }

  public set goldenLayoutPopoutContainer(goldenLayoutPopoutContainer) {
    // please avoid this- set once from golden-layout.ts angular compoennt
    if (this._goldenLayoutPopoutContainer == null) {
      this._goldenLayoutPopoutContainer = goldenLayoutPopoutContainer;
    }
  }

  constructor(
    private profileActionService: ProfilePromiseActionService,
    public translationService: TranslateService,
    protected userSettingsService: BfxUserSettingsService,
    protected activeUserService: ActiveUserProfilePromiseService,
    protected userProfileService: BfxUserProfileService,
  ) {}

  public addDialog(payload: BfxDialogData, body: Element = null) {
    if (body != null) {
      payload.body = body;
    }
    this.dialogSubject.next(payload);
  }

  protected loadActiveUser(res, rej): void {
    this.activeUserService.getActiveUserData().then((activeUser: ActiveUser) => {
      this.loadProfile(res);
    });
  }

  public loadProfileZoomLevel(): Promise<void> {
    return new Promise<void>((res) => {
      this.activeUserService.getActiveUserData().then((activeUser: ActiveUser) => {
        this._ready.resolve();

        if (
          NativeDesktopSupport.isNativeDesktopContainer() &&
          activeUser.profileSettings &&
          activeUser.profileSettings.has(ProfileSettingKeys.userZoomSetting)
        ) {
          let zoomValue: string = activeUser.profileSettings.get(
            ProfileSettingKeys.userZoomSetting,
          );
          let zoom: any[] = ZOOM_VALUES.filter((value: any) => value.value == zoomValue);

          if (zoom.length == 1) {
            let zoomFactor: number = parseFloat(zoom[0].zoom);
            NativeDesktopSupport.setZoomLevel(zoomFactor, true).then(
              () => {
                logger.out.warn(
                  `${this.constructor.name} NativeDesktopSupport.setZoomLevel(${zoomFactor})`,
                );

                NativeDesktopSupport.setReady();
                res();
              },
              (err) => {
                logger.out.warn(
                  `${this.constructor.name} failed to set zoom level %O %O %O`,
                  zoomValue,
                  zoom,
                  err,
                );

                NativeDesktopSupport.setReady();
                res();
              },
            );
          }
        } else {
          NativeDesktopSupport.setReady();
          res();
        }
      });
    });
  }

  private loadProfile(res): void {
    this.getProfileConfig().then((value) => {
      let config = null;

      if (value != "" && value != null) {
        config = JSON.parse(value);
        let additionalConfig: any = config.additionalData;
        additionalConfig = additionalConfig != null ? additionalConfig : {};
        if (
          NativeDesktopSupport.isNativeDesktopContainer() &&
          additionalConfig != null &&
          additionalConfig.singleInstanceLocations != null
        ) {
          // never restore single instance locations on browser
          // as a saved profile from the browser will feature both
          // docked and undocked positions for single instances

          this.singleInstanceLocations = additionalConfig.singleInstanceLocations;
        }

        if (additionalConfig != null && additionalConfig.singleInstanceData != null) {
          this.singleInstanceData = additionalConfig.singleInstanceData;
        }

        delete additionalConfig.singleInstanceLocations;
        delete additionalConfig.singleInstanceData;

        this.additionalProfileConfiguration = additionalConfig;
      }

      res(config);
    });
  }

  // used in order entry
  public triggerShowDebugEvent(refId: string): void {
    this.orderEntrySource.next({
      cmd: OENExternalCommand.OPEN_DEBUG_PANEL,
      data: refId,
    });
  }

  public showSettings(category?: string, event?: Event): void {
    let activeCategory: BfxUserSettingsCategoryIndex;
    let activeSubCategory: any = BfxUserSettingsOrderSubCategoryIndex.Preferences;

    if (category == "General") {
      activeCategory = BfxUserSettingsCategoryIndex.General;
    } else if (category == "Order") {
      activeCategory = BfxUserSettingsCategoryIndex.Order;
    } else if (category == "OrderLadder") {
      activeCategory = BfxUserSettingsCategoryIndex.Order;
    } else if (category == "OrderEntry") {
      activeCategory = BfxUserSettingsCategoryIndex.OrderEntry;
      /*   } else if (category == 'Reporting') {
            activeCategory = BfxUserSettingsCategoryIndex.Reporting; */
    } else if (category == "Notification") {
      activeCategory = BfxUserSettingsCategoryIndex.Notification;
    } else if (category == "Notifications-Application") {
      activeCategory = BfxUserSettingsCategoryIndex.Notification;
      activeSubCategory = BfxUserSettingsNotificationsSubCategoryIndex.Application;
      /*  } else if (category == 'Acceptance') {
            activeCategory = BfxUserSettingsCategoryIndex.Acceptance; */
    } else if (category == "Panic") {
      activeCategory = BfxUserSettingsCategoryIndex.Panic;
    } else if (USERSETTINGS_CATEGORY_BMARKSUMMARY == category) {
      activeCategory = BfxUserSettingsCategoryIndex.BmarkSummary;
    } else {
      activeCategory = BfxUserSettingsCategoryIndex.General;
    }

    let title: string = this.translationService.instant("user.settings.title");
    let componentState: BfxUserSettingsComponentState =
      this.userSettingsService.getComponentState();

    componentState.activeCategory = activeCategory;
    componentState.activeSubCategory = activeSubCategory;

    let config: GLPopoutConfiguration = new GLPopoutConfiguration();

    config.initialState = GLPopoutState.POPOUT;
    config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
    config.popoutResizable = true;
    config.minimumSize = { width: 818, height: 571 };

    if (componentState.componentBounds != null && componentState.componentBounds.height) {
      componentState.componentBounds.height += 39;
      componentState.componentBounds.y -= 33;

      if (componentState.componentBounds.height < config.minimumSize.height) {
        componentState.componentBounds.height = config.minimumSize.height;
      }
    }

    config.position = componentState.componentBounds;
    config.preferredSize = componentState.componentBounds;
    config.sourceEvent = event;

    this.launchFromDashboard(
      title,
      DashboardItemType.UserSettings,
      false,
      GoldenLayoutAdditionMode.DEFAULT,
      config,
    );
  }

  public launchFromDashboard(
    name: string,
    type: DashboardItemType,
    allowMultiples: boolean,
    additionMode: GoldenLayoutAdditionMode,
    config?: GLPopoutConfiguration,
  ): void {
    let descriptor: any = Object.assign(
      {
        name: name,
        type: type,
        allowMultiples: allowMultiples,
        additionMode: additionMode,
      },
      config,
    );

    try {
      if (this.goldenLayout != null) {
        this.goldenLayout.newFromDashboard(descriptor);
      } else {
        logger.out.error(
          "Golden Layout not available yet. Skipping opening component of type: " +
            name +
            " " +
            DashboardItemType[type],
        );
      }
    } catch (e) {
      logger.out.error("Error occurred while opening new dashboard component: " + e);
    }

    // kill order_entry - modify info
    if (
      (type === DashboardItemType.OrderCancel || type === DashboardItemType.DetailFill) &&
      config != null &&
      config.data != null
    ) {
      this.orderEntrySource.next({
        cmd: OENExternalCommand.CANCEL_LAUNCHED,
        data: config.data,
      });
    }
  }

  public popout(
    component: ComponentRef<any>,
    stack: any,
    config: any,
    popout: ComponentRef<GoldenLayoutPopout>,
  ): void {
    let event: GLPopoutEvent = new GLPopoutEvent(component, stack, config, popout);

    let componentId: string = "popout-" + this.popoutCounter++;

    if (config == null) {
      config = component["_config"];
    }

    if (stack) {
      stack._activeContentItem.config[CONFIG_PROPERTY_POPOUT_COMPONENT_ID] = componentId;
    } else if (config && !config[CONFIG_PROPERTY_POPOUT_COMPONENT_ID]) {
      config[CONFIG_PROPERTY_POPOUT_COMPONENT_ID] = componentId;
    }

    this.popouts[componentId] = component;
    this.glPopouts[componentId] = popout?.instance;
    this.goldenLayoutPopoutContainer.onPopoutEvent(event);
  }

  public tab(component: any, title: string): void {
    let tabComp: any = (component?.contentItem?.instance as ComponentRef<any>)?.instance;
    if (tabComp) {
      if (tabComp?.isLadderOrders) {
        if (this.ladderTabs[tabComp.blotterId] !== undefined) {
          let oldTitle: string = this.getOldTitle(this.ladderTabs[tabComp.blotterId].element[0]);
          let oldTooltip: string = this.ladderTabs[tabComp.blotterId].element[0].title;
          component.setTitle(oldTitle);
          component.element[0].title = oldTooltip;
        }
        this.ladderTabs[tabComp.blotterId] = component;
      } else {
        if (this.tabs[tabComp.blotterId] !== undefined) {
          let oldTitle: string = this.getOldTitle(this.tabs[tabComp.blotterId].element[0]);
          let oldTooltip: string = this.tabs[tabComp.blotterId].element[0].title;
          component.setTitle(oldTitle);
          component.element[0].title = oldTooltip;
        }
        this.tabs[tabComp.blotterId] = component;
      }
    }
  }

  private getOldTitle(element: Element): string {
    let oldTitle: string = element.textContent;

    try {
      let qs = element.querySelector(".lm_title");
      if (qs != null) {
        oldTitle = qs.textContent;
      }
    } catch (e) {
      logger.out.warn(`${this.constructor.name}: Cannot determine title, leaving ${oldTitle}`);
    }

    return oldTitle;
  }

  public dock(popoutComponentId: string, config: any): any {
    config.type = config.componentType;
    config = Object.assign(new GLPopoutConfiguration(), config, {
      config: null,
    });

    if (config != null && config.stackId != null) {
      config["docking"] = true;
    } else {
      delete this.popouts[popoutComponentId];
      delete this.glPopouts[popoutComponentId];
    }

    //if (!config.allowMultiples) {
    delete this.singleInstanceLocations[config.componentName];
    //}

    if (this.goldenLayout != null) {
      this.goldenLayout.newFromDashboard(config);
    } else {
      logger.out.warn(
        "Failed to find golden layout service to dock component of type " + config.type,
      );
    }
  }

  public getSingleInstanceConfig(componentName: string): any {
    return this.singleInstanceLocations[componentName];
  }

  public storeSingleInstanceConfig(componentName: string, windowConfig: any): void {
    this.singleInstanceLocations[componentName] = windowConfig;
  }

  public shouldPopout(componentName: string): boolean {
    return (
      this.singleInstanceLocations[componentName] != null &&
      !this.singleInstanceLocations[componentName].docked
    );
  }

  public closeComponent(popoutComponentId: string) {
    // DO NOT MODIFY; or catch failures in place
    this.storeSingleInstanceComponentData(this.popouts[popoutComponentId]);
    delete this.popouts[popoutComponentId];
    delete this.glPopouts[popoutComponentId];
  }

  public storeTabComponent(tabContentItem: any): void {
    if (tabContentItem != null && tabContentItem.instance != null) {
      this.storeSingleInstanceComponentData(tabContentItem.instance);
    }
  }

  public closeTab(componentId: string) {
    // DO NOT MODIFY; or catch failures in place
    delete this.tabs[componentId];
    delete this.ladderTabs[componentId];
  }

  public getSingleInstanceComponentData(componentId: string): void {
    let data: any = this.singleInstanceData[componentId];
    delete this.singleInstanceData[componentId];
    return data;
  }

  protected storeSingleInstanceComponentData(componentRef: ComponentRef<any>): void {
    let config: any = null;

    if (componentRef != null) {
      let componentName: string = null;

      try {
        let componentInstance: any = null;
        if (componentRef != null && componentRef.instance != null) {
          componentInstance = componentRef.instance;
          config = (componentRef as any)._config;
        }

        if (
          config != null &&
          config.allowMultiples === false &&
          config.componentName != null &&
          componentInstance != null &&
          typeof componentInstance.getComponentConfiguration === "function"
        ) {
          componentName = config.componentName;
          this.singleInstanceData[config.componentName] =
            componentInstance.getComponentConfiguration();
        }
      } catch (e) {
        logger.out.error("Failed to store singleton config data: " + componentName);
      }
    }
  }

  public bringPopoutFocus(popoutComponentId: string, componentId: string) {
    let popoutComponent: ComponentRef<any> =
      this.getPopoutComponent(popoutComponentId) != null
        ? this.getPopoutComponent(popoutComponentId)
        : Object.values(this.popouts).find((item) => {
            return popoutComponentId != null
              ? item?.instance?.refId === popoutComponentId
              : item?.["_config"]?.id === componentId;
          });

    if (popoutComponent != null) {
      let popoutComponentId = popoutComponent?.instance?.refId;
      let popout = this.getPopout(popoutComponentId);
      this.goldenLayoutPopoutContainer.onPopoutFocus(popoutComponentId, popoutComponent, popout);
    } else {
      logger.out.warn(
        `${this.constructor.name}.bringPopoutFocus(` +
          `popoutComponentId:${popoutComponentId}, componentId:${componentId}) could not find popout component`,
      );
    }
  }

  public getPopoutComponent(popoutComponentId: string): ComponentRef<any> {
    return this.popouts[popoutComponentId];
  }

  public getPopout(popoutComponentId: string): GoldenLayoutPopout {
    return this.glPopouts[popoutComponentId];
  }

  public deletePopoutComponent(popoutComponentId: string): void {
    if (popoutComponentId != null && this.popouts[popoutComponentId] != null) {
      let component: any = this.popouts[popoutComponentId];
      let rootNodeElement: Element = component.location.nativeElement.firstElementChild as Element;
      if (rootNodeElement != null && rootNodeElement instanceof Element) {
        NativeDesktopSupport.resizeObserver.unobserve(rootNodeElement);
      }
    }
    delete this.popouts[popoutComponentId];
    delete this.glPopouts[popoutComponentId];
  }

  public getPopoutComponents(): Array<ComponentRef<any>> {
    return Object.keys(this.popouts).map((key: string) => this.popouts[key]);
  }

  public getPopoutComponentIds(): Array<string> {
    return Object.keys(this.popouts);
  }

  public getLayoutContent(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.componentStateFn) {
        reject("getLayoutContent failed componentStateFn not set");
      }
      this.componentStateFn().then(
        (layoutContent: any) => {
          layoutContent.additionalData = this.additionalProfileConfiguration;
          layoutContent.additionalData.singleInstanceLocations = this.singleInstanceLocations;
          layoutContent.additionalData.singleInstanceData = this.singleInstanceData;

          let content: string = null;
          try {
            content = JSON.stringify(layoutContent);
            resolve(content);
          } catch (err) {
            logger.out.error("Error saving component configuration.", err);
            reject(err);
          }
        },
        (err: any) => {
          logger.out.info("getLayoutContent failed: " + err);
          reject(err);
        },
      );
    });
  }

  public getProfileConfiguration(): Promise<any> {
    return this.profileConfiguration;
  }

  public registerComponentState(fnArg: Function) {
    this.componentStateFn = fnArg;
  }

  public updateBadge(component: any, badgeCount: number, state: GLComponentAlertStatus) {
    this.subjectBadge.next(new GLBadgeUpdateEvent(component, badgeCount, state));
  }

  public forceFlashUpdate(): void {
    this.subjectFlash.next();
  }

  public getAdditionalProfileConfiguration(): object {
    return this.additionalProfileConfiguration;
  }

  public getAdditionalProfileConfigurationValue(name: string): object {
    if (name && this.additionalProfileConfiguration && this.additionalProfileConfiguration[name]) {
      return this.additionalProfileConfiguration[name];
    }

    return null;
  }

  saveProfileConfig(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.getLayoutContent().then(
        (layoutContent) => {
          let profileSettings: Array<ProfileSettingImpl> = [];
          profileSettings.push(
            this.userProfileService.createProfileSettingObject(
              ProfileSettingKeys.userProfileGLContent,
              layoutContent,
            ),
          );
          this.profileActionService.updateProfile(profileSettings, false).then(
            (res) => {
              this.profileActionSubscription.next(true);
              resolve();
            },
            (reason) => {
              logger.out.info("profile not saved " + <any>reason);
              this.profileActionSubscription.next(false);
              reject(reason);
            },
          );
        },
        (reason) => {
          reject(reason);
        },
      );
    });
  }

  getProfileConfig(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.profileActionService
        .getProfileConfig(ProfileSettingKeys.userProfileGLContent)
        .then((configValue) => {
          resolve(configValue);
        }),
        (reason) => {
          logger.out.error("ERROR: ConfigService.getConfig() = PROMISE ERROR:", <any>reason);
          reject(reason);
        };
    });
  }

  public updateOrderBlotterTabs(title: string, blotterId: string, extendedTypeId: any): void {
    blotterId =
      blotterId === "BfxHedgeOrderMonitorComponent" && blotterId != null
        ? "BmarkSummaryContainerComponent"
        : blotterId; // in order to sync bmark blotters the summary container should only change
    if (this.tabs[blotterId]) {
      const isBmarkSummary = extendedTypeId === 1;
      const isAlgoMonitor = extendedTypeId === 2;

      let titleLong = isBmarkSummary ? BM_TAB_TITLE : OB_TAB_TITLE;
      let titleShort = isBmarkSummary ? BM_TAB_TITLE_SHORT : OB_TAB_TITLE_SHORT;

      if (isAlgoMonitor) {
        titleLong = AM_TAB_TITLE;
        titleShort = AM_TAB_TITLE_SHORT;
      }

      this.setBlotterTitle(this.tabs, title, blotterId, titleLong, titleShort);
    } else if (this.ladderTabs[blotterId]) {
      this.updateOrderLadderTabs(title, blotterId);
    }
  }

  public updateClientTradeBlotterTabs(title: string, blotterId: string): void {
    if (this.tabs[blotterId]) {
      this.setBlotterTitle(this.tabs, title, blotterId, CT_TAB_TITLE, CT_TAB_TITLE_SHORT);
    }
  }

  updateRiskTradeBlotterTabs(title: string, blotterId: string) {
    if (this.tabs[blotterId]) {
      this.setBlotterTitle(this.tabs, title, blotterId, RT_TAB_TITLE, RT_TAB_TITLE_SHORT);
    }
  }

  public updateInterventionBlotterTabs(title: string, blotterId: string): void {
    if (this.tabs[blotterId]) {
      this.setBlotterTitle(this.tabs, title, blotterId, DI_TAB_TITLE, DI_TAB_TITLE_SHORT);
    }
  }

  public updateOrderLadderTabs(title: string, blotterId: string): void {
    this.setBlotterTitle(this.ladderTabs, title, blotterId, OL_TAB_TITLE, OL_TAB_TITLE_SHORT);
  }

  private setBlotterTitle(
    tabArray: NullProtoObject<any>,
    title: any,
    blotterId: string,
    longTitle: string,
    shortTitle: string,
  ) {
    if (title.length == 0) {
      tabArray[blotterId].setTitle(longTitle);
    } else {
      tabArray[blotterId].setTitle(shortTitle + " - " + title);
      tabArray[blotterId].element[0].title = longTitle + " - " + title;
    }

    this.titleUpdateSubject.next(blotterId);
  }

  public showInbox(preventActivation: boolean): void {
    const title = "Inbox";
    const type = DashboardItemType.Inbox;
    this.checkShownOrLaunch(title, type, preventActivation);
  }

  public showIntervention(preventActivation: boolean): void {
    const title = "Dealer Intervention";
    const type = DashboardItemType.InterventionBlotter;
    this.checkShownOrLaunch(title, type, preventActivation);
  }

  public showBenchmarkSummary(preventActivation: boolean): void {
    let title = "Benchmark Summary";
    let type = DashboardItemType.BenchmarkSummary;
    this.checkShownOrLaunch(title, type, preventActivation);
  }

  public showOpsInt(preventActivation: boolean) {
    let title = "Ops Intervention";
    let type = DashboardItemType.OpsIntBlotter;
    this.checkShownOrLaunch(title, type, preventActivation);
  }

  public showConnectionMonitor(preventActivation: boolean): void {
    let title = "Connection Monitor";
    let type = DashboardItemType.ConnectionMonitor;
    this.checkShownOrLaunch(title, type, preventActivation);
  }

  /**
   *
   * @param title
   * @param type
   * @param preventActivation controls what happens to a minimized or backgrounded
   */
  private checkShownOrLaunch(title: string, type: DashboardItemType, preventActivation: boolean) {
    let shown = false;

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      let found: NativeDesktopWindow = NativeDesktopSupport.getWindowById(title);

      if (found != null) {
        if (preventActivation !== true) {
          found.restoreToFront();
        }
        shown = true;
      }
    }
    if (!shown) {
      let config: GLPopoutConfiguration = new GLPopoutConfiguration({
        preventActivation: preventActivation,
        restoreMainWindow: DashboardItemType.OpsIntBlotter === type,
      });
      this.launchFromDashboard(title, type, false, GoldenLayoutAdditionMode.DEFAULT, config);
    }
  }

  public restoreWindowAsPopout(
    wndId: string,
    preventActivation: boolean = false,
  ): Promise<boolean> {
    let wnd = null;

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      wnd = NativeDesktopSupport.getWindowById(wndId);
    }

    if (wnd != null && preventActivation !== true) {
      wnd.restoreToFront();
    }

    return Promise.resolve(false);
  }

  public isReady(): Promise<void> {
    return this._ready.get<void>();
  }

  public goldenLayoutLoaded(): void {
    this._ready.resolve();
  }

  public getNotificationConfig(): any {
    return this.notificationData;
  }

  public setNotificationConfig(data: any, publish: boolean = true): void {
    this.notificationData = data;
    if (publish === true) {
      this.notificationDataSubject.next(data);
    }
  }
}
